/**
 * Param-schema dung chung (1 nguon) cho ca Admin SPA (render form kiem thu),
 * Gateway (seed backfill + catalog cong khai). Tach khoi index.ts cho gon.
 *
 * Day la "fallback theo code" — nguon chan ly khi chay la cot `paramsSchema`
 * trong DB (sua trong admin). getModelParams() chi dung khi model chua co
 * paramsSchema (model cu / them moi chua nhap).
 */

export type ParamType =
  | 'textarea'
  | 'text'
  | 'number'
  | 'select'
  | 'bool'
  | 'json'
  | 'color'
  | 'image'
  | 'video'
  | 'audio'
  | 'media'
  | 'image[]'
  | 'video[]';

export interface ParamField {
  name: string;
  label: string;
  type: ParamType;
  options?: (string | number)[];
  default?: unknown;
  required?: boolean;
  max?: number;
  /** false = an thong so (khong expose ra catalog/khong gui). Mac dinh (undefined) = hien. */
  enabled?: boolean;
  /** Chi hien field nay khi field khac bang gia tri cho truoc (vd PlanX khi mode=planx). */
  showWhen?: { field: string; equals: unknown };
}

/** Shape toi thieu de resolve param theo code/endpoint (ProviderModel thoa man cau truc nay). */
export interface ModelParamsInput {
  code: string;
  submitEndpoint?: string | null;
  config?: Record<string, any> | null;
}

const PROMPT: ParamField = { name: 'prompt', label: 'Prompt', type: 'textarea', required: true };
const NEG: ParamField = { name: 'negative_prompt', label: 'Negative prompt', type: 'textarea' };

// ---- Dola (theo Dola_API_Webhook.md muc 4) ----
const DOLA_RATIO: ParamField = {
  name: 'ratio', label: 'Tỷ lệ (ratio)', type: 'select',
  options: ['1:1', '16:9', '9:16', '4:3', '3:4', '2:3', '3:2', '21:9'], default: '1:1',
};
const DOLA_STYLE: ParamField = {
  name: 'style', label: 'Phong cách (style)', type: 'select',
  options: [
    'cinematic', 'landscape', 'japanese_anime', '3d_anime', 'cyberpunk', 'oil_painting',
    'watercolor', 'flat_illustration', 'childrens_illustration', 'pixel_art', 'colored_pencil',
    'ink_wash_painting', 'ink_printing',
  ],
};
const DOLA_SCENE: ParamField = {
  name: 'scene', label: 'Bối cảnh (scene)', type: 'select',
  options: ['default', 'write_t2i', 'img_regen', 'otter_page_t2i'], default: 'default',
};
const DOLA_TASK = (def: string): ParamField => ({
  name: 'task', label: 'Tác vụ (task)', type: 'select',
  options: ['generate', 'regenerate', 'regenerate_single', 'edit', 'inpainting', 'outpainting', 'smearing', 'super_resolution', 'operate_background'],
  default: def,
});
const DOLA_REFS: ParamField = {
  // Mang URL anh tham chieu (toi da 10, tai lieu muc 7) — dispatch tu boc thanh { url, refer_type }.
  name: 'reference_images', label: 'Ảnh tham chiếu (reference_images)', type: 'image[]', max: 10,
};

// ---- Dola VIDEO (POST /api/v1/videos/generate — body PHANG, khong boc `request`) ----
// ratio CHI nhan 3 gia tri (khac hoan toan bang ratio cua anh). duration 5 | 10 | 15 giay.
const DOLA_VIDEO_MODEL: ParamField = {
  name: 'model', label: 'Model video (xem GET /api/v1/videos/abilities)', type: 'text',
  default: 'seedance_2.0_fast',
};
const DOLA_VIDEO_DURATION: ParamField = {
  name: 'duration', label: 'Thời lượng (giây)', type: 'select', options: [5, 10, 15], default: 10,
};
const DOLA_VIDEO_RATIO: ParamField = {
  name: 'ratio', label: 'Tỷ lệ (ratio)', type: 'select', options: ['9:16', '16:9', '1:1'], default: '9:16',
};
const DOLA_VIDEO_REFS: ParamField = {
  // Toi da 10 anh — dispatch upload qua /api/v1/videos/upload-reference roi gui { key }.
  name: 'reference_images', label: 'Ảnh tham chiếu (image-to-video, tối đa 10)', type: 'image[]', max: 10,
};

// ---- Dreamface (lipsync — webhook, multipart). Theo DREAMFACE_API_WEBHOOK.md muc 1 ----
//  POST /api/v1/jobs: media(anh/video khuon mat) + audio + resolution + session_id?.
const DREAMFACE_MEDIA: ParamField = {
  name: 'media', label: 'Ảnh/Video khuôn mặt (media)', type: 'media', required: true,
};
const DREAMFACE_AUDIO: ParamField = {
  name: 'audio', label: 'Audio (audio)', type: 'audio', required: true,
};
const DREAMFACE_RES: ParamField = {
  name: 'resolution', label: 'Độ phân giải', type: 'select',
  options: ['480p', '720p', '1080p', '2K', '4K'], default: '720p',
};
const DREAMFACE_SESSION: ParamField = {
  name: 'session_id', label: 'Session ID (tuỳ chọn — trống = tự luân phiên)', type: 'text',
};

// ---- GPT Playwright (ChatGPT, RutgonGPT — webhook). Theo ChatGPT_webhook_integration_new.md muc 3 + 7 ----
//  1 endpoint async /api/v1/chat/completions cho TAT CA che do; `mode` chon hanh vi.
//  7 mode that (data.mode): chat · chat-fast · chat-fast-vision · image · image-fast · image-edit · image-compose.
//  `image-auto` la INPUT ALIAS (server tu route theo so anh: 0→fast, 1→edit, ≥2→compose).
//  Truong: prompt(*) · mode · model(chat*) · accountId(chat/image Playwright) · count(image-*, 1-4) ·
//          size(image) · gizmo_id(chat Playwright) · images(multipart, ≤10).
const GPTPW_IMAGES: ParamField = { name: 'images', label: 'Ảnh đính kèm (0..N, multimodal)', type: 'image[]', max: 10 };
const GPTPW_COUNT: ParamField = { name: 'count', label: 'Số ảnh kết quả (count, 1-4)', type: 'select', options: [1, 2, 3, 4], default: 1 };
const GPTPW_SIZE: ParamField = {
  name: 'size', label: 'Tỷ lệ (size)', type: 'select',
  // 'auto' = de nguon tu chon (gui len API = bo trong). Cac ti le con lai theo tai lieu:
  // 1:1, 3:4, 9:16, 4:3, 16:9.
  options: ['auto', '1:1', '3:4', '9:16', '4:3', '16:9'], default: 'auto',
};
const GPTPW_ACCOUNT: ParamField = { name: 'accountId', label: 'Account (accountId — chỉ Playwright)', type: 'text', default: 'auto' };
// Tuy chon: khi accountId = 'auto'/rong, gui kem group_id -> nguon chi luan phien trong CAC
// tai khoan thuoc nhom nay thay vi ca pool. Khong truyen = giu hanh vi cu (luan phien ca pool).
const GPTPW_GROUP: ParamField = { name: 'group_id', label: 'Nhóm tài khoản (group_id — tuỳ chọn)', type: 'text' };
const GPTPW_MODEL: ParamField = { name: 'model', label: 'Model ChatGPT (model)', type: 'text', default: 'auto' };
const GPTPW_GIZMO: ParamField = { name: 'gizmo_id', label: 'MyGPT (gizmo_id — chỉ chat Playwright)', type: 'text' };
// Bien the field anh theo che do (so luong khac nhau).
const GPTPW_IMAGES_VISION: ParamField = { name: 'images', label: 'Ảnh để hỏi-đáp (1..N)', type: 'image[]', max: 10, required: true };
const GPTPW_IMAGES_AUTO: ParamField = { name: 'images', label: 'Ảnh (0..4 → tự route fast/edit/compose)', type: 'image[]', max: 4 };
const GPTPW_IMAGES_EDIT: ParamField = { name: 'images', label: 'Ảnh cần sửa (đúng 1 ảnh)', type: 'image[]', max: 1, required: true };
const GPTPW_IMAGES_COMPOSE: ParamField = { name: 'images', label: 'Ảnh để ghép (2..4 ảnh)', type: 'image[]', max: 4, required: true };

// ---- Zapcap (caption video — webhook, body phang). Theo zapcap_Webhook_API.md ----
//  Theme/Sound = select tinh theo slug tai lieu (co the doi phia nguon → cap nhat tay).
const ZAPCAP_THEME: ParamField = {
  name: 'theme', label: 'Theme (slug)', type: 'select',
  options: ['viktor', 'tracy', 'hormozi_1'], default: 'viktor', required: true,
};
const ZAPCAP_LANG: ParamField = {
  name: 'language', label: 'Ngôn ngữ transcribe', type: 'select',
  options: ['vi', 'en', 'ja', 'ko', 'zh', 'es', 'fr', 'de'], default: 'vi',
};
const ZAPCAP_COLOR: ParamField = { name: 'color', label: 'Màu chữ', type: 'color' };
const ZAPCAP_SIZE: ParamField = { name: 'size', label: 'Cỡ chữ (8–120)', type: 'number' };
const ZAPCAP_POSY: ParamField = { name: 'position_y', label: 'Vị trí dọc (0–100)', type: 'number' };
const ZAPCAP_HL_MAIN: ParamField = { name: 'highlight_main', label: 'Highlight chính', type: 'color' };
const ZAPCAP_HL_SECOND: ParamField = { name: 'highlight_second', label: 'Highlight phụ', type: 'color' };
const ZAPCAP_HL_THIRD: ParamField = { name: 'highlight_third', label: 'Highlight 3', type: 'color' };
const ZAPCAP_TEXT_EDITS: ParamField = {
  name: 'text_edits', label: 'text_edits (JSON: [{find,replace}])', type: 'json',
};
const ZAPCAP_WORD_EMOJIS: ParamField = {
  name: 'word_emojis', label: 'word_emojis (JSON: [{word,emoji}])', type: 'json',
};
const ZAPCAP_VIDEO: ParamField = {
  name: 'video_url', label: 'Video (tải lên hoặc dán URL)', type: 'video', required: true,
};
const ZAPCAP_PROJECT: ParamField = { name: 'project_id', label: 'Project ID', type: 'text', required: true };

// ---- Watermark (xoa watermark/logo — webhook, multipart). Theo watermark_API_Webhook.md muc 3.2 ----
//  POST /api/v1/jobs: file(video/anh, bat buoc) + template HOAC params/tung field.
//  Vung watermark luu theo ti le khung nen template dung cho moi do phan giai cung ti le.
const WMK_FILE: ParamField = {
  name: 'file', label: 'Video/Ảnh cần xoá watermark (file)', type: 'media', required: true,
};
const WMK_TEMPLATE: ParamField = {
  // Danh sach template lay dong tu GET /api/v1/templates (admin bam "Cập nhật template").
  name: 'template', label: 'Template (id hoặc tên — khuyến nghị)', type: 'text', required: true,
};
const WMK_METHOD: ParamField = {
  name: 'method', label: 'Phương pháp xoá', type: 'select',
  options: ['smart', 'inpaint', 'blur'], default: 'smart',
};
const WMK_SENSITIVITY: ParamField = {
  name: 'sensitivity', label: 'Độ nhạy (chỉ với smart)', type: 'select',
  options: ['low', 'medium', 'high'], default: 'medium',
};
const WMK_SHAPE: ParamField = {
  name: 'shape', label: 'Hình dạng vùng', type: 'select',
  options: ['rect', 'ellipse', 'diamond', 'poly'], default: 'rect',
};
const WMK_X: ParamField = { name: 'x', label: 'X vùng watermark (px)', type: 'number' };
const WMK_Y: ParamField = { name: 'y', label: 'Y vùng watermark (px)', type: 'number' };
const WMK_W: ParamField = { name: 'width', label: 'Chiều rộng vùng (px)', type: 'number' };
const WMK_H: ParamField = { name: 'height', label: 'Chiều cao vùng (px)', type: 'number' };
const WMK_POINTS: ParamField = {
  name: 'points', label: 'points (JSON: [[x,y], ...] — bắt buộc khi shape=poly)', type: 'json',
};

// ---- Videogen (tao video t2v/i2v — webhook, body phang, submit JSON). Theo API_Webhook_Videogen.md ----
//  `/generate` body phang: model(*) · prompt(*) · duration · resolution · aspect_ratio ·
//  image_url (start frame, i2v) · end_image_url (end frame) · video_url (model motion) · reference_images.
//  Cac enum (duration/resolution/...) co the doi phia nguon -> /models tra dac ta dong; o day la default tinh.
const VIDEOGEN_PROMPT: ParamField = { name: 'prompt', label: 'Prompt', type: 'textarea', required: true };
const VIDEOGEN_DURATION: ParamField = {
  name: 'duration', label: 'Thời lượng (giây)', type: 'select', options: [4, 6, 8], default: 4,
};
const VIDEOGEN_RESOLUTION: ParamField = {
  name: 'resolution', label: 'Độ phân giải', type: 'select', options: ['720p', '1080p'], default: '720p',
};
const VIDEOGEN_RATIO: ParamField = {
  name: 'aspect_ratio', label: 'Tỷ lệ khung', type: 'select', options: ['16:9', '9:16', '1:1'], default: '16:9',
};
const VIDEOGEN_IMAGE: ParamField = {
  name: 'image_url', label: 'Ảnh đầu (start frame — tuỳ chọn, i2v)', type: 'image',
};
const VIDEOGEN_END_IMAGE: ParamField = {
  name: 'end_image_url', label: 'Ảnh cuối (end frame — tuỳ chọn)', type: 'image',
};

// ---- FlipAI video generation (theo /api-docs.html) ----
const FLIPAI_ASPECT: ParamField = {
  name: 'aspect_ratio', label: 'Tỉ lệ khung', type: 'select', options: ['16:9', '9:16'], default: '16:9',
};
const FLIPAI_COUNT: ParamField = {
  name: 'count', label: 'Số video mỗi request (1–4)', type: 'number', default: 1,
};
const FLIPAI_DURATION: ParamField = {
  name: 'duration', label: 'Thời lượng video', type: 'select', options: ['4s', '6s', '8s'], default: '8s',
};

// Cau hinh tham so theo tung model (rut tu tai lieu Plenxai). Co gia tri cho phep
// -> hien thi dropdown de chon; anh/video -> nut tai len.
const BY_CODE: Record<string, ParamField[]> = {
  // ---------- FlipAI (2 model trung gian nhu Google Flow) ----------
  // Frames tu route: text-only -> T2V, start -> I2V, start+end -> Batch Frame.
  frames: [
    PROMPT,
    { name: 'start_image', label: 'Start frame (optional)', type: 'image' },
    { name: 'end_image', label: 'End frame (optional)', type: 'image' },
    FLIPAI_ASPECT,
    FLIPAI_COUNT,
    FLIPAI_DURATION,
  ],
  ingredients: [
    PROMPT,
    { name: 'images', label: 'Reference images (1-3)', type: 'image[]', max: 3, required: true },
    FLIPAI_ASPECT,
    FLIPAI_COUNT,
    FLIPAI_DURATION,
  ],
  'veo-text-to-video': [PROMPT, FLIPAI_ASPECT, FLIPAI_COUNT, FLIPAI_DURATION],
  'veo-image-to-video': [
    PROMPT,
    { name: 'image', label: 'Ảnh đầu vào (bắt buộc)', type: 'image', required: true },
    { ...FLIPAI_ASPECT, default: '9:16' },
    FLIPAI_COUNT,
    FLIPAI_DURATION,
  ],
  'veo-multi-image-to-video': [
    PROMPT,
    { name: 'images', label: 'Ảnh tham chiếu (bắt buộc, tối đa 9)', type: 'image[]', max: 9, required: true },
    FLIPAI_ASPECT,
    FLIPAI_COUNT,
    FLIPAI_DURATION,
  ],
  'veo-batch-frame': [
    PROMPT,
    { name: 'start_image', label: 'Ảnh đầu (bắt buộc)', type: 'image', required: true },
    { name: 'end_image', label: 'Ảnh cuối (bắt buộc)', type: 'image', required: true },
    FLIPAI_ASPECT,
    FLIPAI_COUNT,
    FLIPAI_DURATION,
  ],

  // ---------- Dola (webhook) ----------
  'dola-generate': [PROMPT, DOLA_TASK('generate'), DOLA_RATIO, DOLA_STYLE, DOLA_SCENE, DOLA_REFS],
  'dola-edit': [PROMPT, DOLA_TASK('edit'), DOLA_RATIO, DOLA_STYLE, DOLA_SCENE, DOLA_REFS],
  // Video: endpoint rieng /api/v1/videos/generate, KHONG co task/style/scene.
  'dola-video': [PROMPT, DOLA_VIDEO_MODEL, DOLA_VIDEO_DURATION, DOLA_VIDEO_RATIO, DOLA_VIDEO_REFS],

  // ---------- Zapcap (caption video, webhook) ----------
  'zapcap-captions': [
    ZAPCAP_VIDEO, ZAPCAP_THEME, ZAPCAP_LANG, ZAPCAP_COLOR, ZAPCAP_SIZE, ZAPCAP_POSY,
    ZAPCAP_HL_MAIN, ZAPCAP_HL_SECOND, ZAPCAP_HL_THIRD, ZAPCAP_TEXT_EDITS, ZAPCAP_WORD_EMOJIS,
  ],
  // widget_overrides/chunk_overrides KHONG khai bao o day: build tu editor caption (xem ModelTestTab).
  'zapcap-render': [
    ZAPCAP_PROJECT, ZAPCAP_THEME, ZAPCAP_COLOR, ZAPCAP_SIZE, ZAPCAP_POSY,
    ZAPCAP_HL_MAIN, ZAPCAP_HL_SECOND, ZAPCAP_HL_THIRD,
    ZAPCAP_TEXT_EDITS, ZAPCAP_WORD_EMOJIS,
  ],

  // ---------- Dreamface (lipsync, webhook) ----------
  'dreamface-lipsync': [DREAMFACE_MEDIA, DREAMFACE_AUDIO, DREAMFACE_RES, DREAMFACE_SESSION],

  // ---------- Watermark (xoa watermark/logo, webhook, multipart) ----------
  // 2 model = 2 cach cau hinh cua CUNG endpoint POST /api/v1/jobs (uu tien template > params).
  'watermark-template': [WMK_FILE, WMK_TEMPLATE],
  'watermark-manual': [WMK_FILE, WMK_METHOD, WMK_SENSITIVITY, WMK_SHAPE, WMK_X, WMK_Y, WMK_W, WMK_H, WMK_POINTS],

  // ---------- Videogen (tao video, webhook). 1 model = t2v + i2v (image_url tuy chon) ----------
  'veo-fast': [
    VIDEOGEN_PROMPT, VIDEOGEN_DURATION, VIDEOGEN_RESOLUTION, VIDEOGEN_RATIO,
    VIDEOGEN_IMAGE, VIDEOGEN_END_IMAGE,
  ],

  // ---------- GPT Playwright (ChatGPT — webhook). 8 code = 8 mode submit ----------
  // Tat ca submit -> /api/v1/chat/completions (async); `mode` nhung san trong model.config.requestDefaults.
  // -- Chat --
  'chat': [PROMPT, GPTPW_IMAGES, GPTPW_MODEL, GPTPW_ACCOUNT, GPTPW_GROUP, GPTPW_GIZMO], // Playwright: text + 0..N anh multimodal
  'chat-fast': [PROMPT, GPTPW_MODEL], // chatgpt2api: text thuan, KHONG ho tro anh
  'chat-fast-vision': [PROMPT, GPTPW_IMAGES_VISION, GPTPW_MODEL], // chatgpt2api + anh (OpenAI Vision)
  // -- Image --
  'image': [PROMPT, GPTPW_IMAGES, GPTPW_SIZE, GPTPW_ACCOUNT, GPTPW_GROUP], // Playwright: 0..N anh -> URL anh
  'image-auto': [PROMPT, GPTPW_IMAGES_AUTO, GPTPW_COUNT, GPTPW_SIZE], // chatgpt2api: server tu route theo so anh
  'image-fast': [PROMPT, GPTPW_COUNT, GPTPW_SIZE], // chatgpt2api: 0 anh -> sinh moi
  'image-edit': [PROMPT, GPTPW_IMAGES_EDIT, GPTPW_COUNT, GPTPW_SIZE], // chatgpt2api: 1 anh -> sua
  'image-compose': [PROMPT, GPTPW_IMAGES_COMPOSE, GPTPW_COUNT, GPTPW_SIZE], // chatgpt2api: >=2 anh -> ghep
  'auto_gpt2': [PROMPT, GPTPW_IMAGES, GPTPW_COUNT, GPTPW_SIZE, GPTPW_MODEL, GPTPW_GROUP], // Trung gian & Fallback
  // Model cu (sai theo tai lieu moi) — giu schema fallback phong khi admin bat lai; mac dinh da set inactive o seed.
  'gpt-playwright-generate': [PROMPT, GPTPW_IMAGES, GPTPW_COUNT, GPTPW_SIZE, GPTPW_ACCOUNT, GPTPW_GROUP],
  'gpt-playwright-ask': [PROMPT, GPTPW_IMAGES, GPTPW_MODEL, GPTPW_ACCOUNT, GPTPW_GROUP],

  // ---------- ZORA (OpenAI-compatible, dong bo) ----------
  // response_format/upload_to_storage KHONG expose — adapter tu dat mac dinh.
  // size = pixel canvas hoac auto theo tai lieu ZORA; adapter se map gia tri cu ve canvas hop le.
  'gpt-img-2': [
    PROMPT,
    { name: 'n', label: 'Số ảnh (n)', type: 'number', default: 1, max: 1 },
    {
      name: 'size', label: 'Kích thước (size)', type: 'select',
      options: ['1024x1024', '1024x1536', '1536x1024', 'auto'], default: '1024x1024',
    },
    { name: 'image_url', label: 'Ảnh tham chiếu (img2img — tối đa 4)', type: 'image[]', max: 4 },
  ],

  // ---------- Zonetoken ----------
  'gpt-5.5': [
    PROMPT,
    { name: 'system_instruction', label: 'System instruction', type: 'textarea' },
    { name: 'temperature', label: 'Temperature (0–2)', type: 'number', default: 0.7 },
    { name: 'images', label: 'Ảnh (multi-modal)', type: 'image[]' },
  ],
  'gpt-image-2-pro-max': [
    PROMPT,
    { name: 'n', label: 'Số ảnh (n)', type: 'number', default: 1, max: 20 },
    {
      name: 'size', label: 'Tỷ lệ (size)', type: 'select',
      options: ['auto', '1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '21:9'], default: 'auto',
    },
    { name: 'image_url', label: 'Ảnh tham chiếu (img2img — nhiều ảnh)', type: 'image[]', max: 10 },
  ],

  // ---------- Tao anh ----------
  'gpt-image-2': [
    PROMPT,
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['low', 'medium', 'high'], default: 'low' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['Auto', '1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'] },
    { name: 'references_urls', label: 'Ảnh tham chiếu (references_urls)', type: 'image[]', max: 3 },
    NEG,
    { name: 'server', label: 'Server', type: 'select', options: ['vip-01', 'business'] },
  ],
  'imagen-3-5': [
    PROMPT,
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['1k', '2k', '4k'], default: '1k' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['1:1', '4:3', '16:9', '9:16', '3:4'] },
    NEG,
  ],
  'nano-banana-2': [
    PROMPT,
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['1k', '2k', '4k'], default: '1k' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['Auto', '1:1', '4:3', '16:9', '21:9', '3:2', '9:16', '3:4'] },
    { name: 'references_urls', label: 'Ảnh tham chiếu (references_urls)', type: 'image[]', max: 8 },
    NEG,
  ],
  'nano-banana-pro': [
    PROMPT,
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['1k', '2k', '4k'], default: '1k' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['Auto', '1:1', '4:3', '3:4', '9:16', '16:9'] },
    { name: 'references_urls', label: 'Ảnh tham chiếu (references_urls)', type: 'image[]', max: 8 },
    NEG,
  ],
  'seedream-4-5': [
    PROMPT,
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['2k', '1k'], default: '2k' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['1:1', '4:3', '3:4', '2:3', '21:9', '4:5', '5:4', '9:16', '16:9', '3:2'] },
    NEG,
    { name: 'server', label: 'Server', type: 'select', options: ['vip-01', 'business'] },
  ],

  // ---------- Tao video (generate/video) ----------
  'veo-3-relaxed': [
    PROMPT,
    { name: 'mode', label: 'Mode', type: 'select', options: ['t2v', 'i2v'], default: 't2v' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [4, 6, 8], default: 4 },
    { name: 'quality', label: 'Quality', type: 'select', options: ['720p', '1080p', '4K'], default: '720p' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['16:9', '9:16'], default: '16:9' },
    { name: 'start_image_url', label: 'Ảnh Start Frame', type: 'image' },
    { name: 'end_image_url', label: 'Ảnh End Frame', type: 'image' },
  ],
  'veo-3-quality': [
    PROMPT,
    { name: 'mode', label: 'Mode', type: 'select', options: ['t2v', 'i2v'], default: 't2v' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [4, 6, 8], default: 4 },
    { name: 'quality', label: 'Quality', type: 'select', options: ['720p', '1080p', '4K'], default: '720p' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['16:9', '9:16'], default: '16:9' },
    { name: 'start_image_url', label: 'Ảnh Start Frame', type: 'image' },
    { name: 'end_image_url', label: 'Ảnh End Frame', type: 'image' },
  ],
  'veo-3-fast': [
    PROMPT,
    { name: 'mode', label: 'Mode', type: 'select', options: ['t2v', 'i2v'], default: 't2v' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [4, 6, 8], default: 4 },
    { name: 'quality', label: 'Quality', type: 'select', options: ['720p', '1080p', '4K'], default: '720p' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['16:9', '9:16'], default: '16:9' },
    { name: 'start_image_url', label: 'Ảnh Start Frame', type: 'image' },
    { name: 'end_image_url', label: 'Ảnh End Frame', type: 'image' },
  ],
  'kling-3.0': [
    PROMPT,
    { name: 'mode', label: 'Mode', type: 'select', options: ['i2v'], default: 'i2v' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [3, 5, 6, 7, 8, 10, 12, 15] },
    { name: 'quality', label: 'Quality', type: 'select', options: ['720p', '1080p'], default: '720p' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'] },
    { name: 'start_image_url', label: 'Ảnh Start Frame', type: 'image' },
    { name: 'end_image_url', label: 'Ảnh End Frame', type: 'image' },
    { name: 'server', label: 'Server', type: 'select', options: ['vip-03', 'cheap', 'business'] },
  ],
  'kling-3-omni': [
    PROMPT,
    { name: 'mode', label: 'Mode', type: 'select', options: ['t2v', 'i2v'], default: 't2v' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [3, 5, 6, 7, 8, 10, 12, 15] },
    { name: 'quality', label: 'Quality', type: 'select', options: ['720p', '1080p', '4k'], default: '720p' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['1:1', '9:16', '4:3', '3:4', '16:9'] },
    { name: 'start_image_url', label: 'Ảnh Start Frame', type: 'image' },
    { name: 'end_image_url', label: 'Ảnh End Frame', type: 'image' },
    { name: 'server', label: 'Server', type: 'select', options: ['vip-01', 'business'] },
  ],
  'kling-2.6': [
    PROMPT,
    { name: 'mode', label: 'Mode', type: 'select', options: ['i2v'], default: 'i2v' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [5, 10] },
    { name: 'quality', label: 'Quality', type: 'select', options: ['720p', '1080p'], default: '720p' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['16:9', '9:16'] },
    { name: 'start_image_url', label: 'Ảnh Start Frame', type: 'image' },
    { name: 'end_image_url', label: 'Ảnh End Frame', type: 'image' },
    { name: 'server', label: 'Server', type: 'select', options: ['business', 'vip-02'] },
  ],
  'grok-video': [
    PROMPT,
    { name: 'mode', label: 'Mode', type: 'select', options: ['t2v', 'i2v'], default: 't2v' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [6, 10] },
    { name: 'quality', label: 'Quality', type: 'select', options: ['720p'], default: '720p' },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['16:9', '9:16', '1:1'] },
    { name: 'start_image_url', label: 'Ảnh Start Frame', type: 'image' },
    { name: 'end_image_url', label: 'Ảnh End Frame', type: 'image' },
    { name: 'server', label: 'Server', type: 'select', options: ['business', 'vip-01'] },
  ],

  // ---------- Video Omni (generate/video-omni) ----------
  'veo-3.1-fast': [
    PROMPT,
    { name: 'reference_images', label: 'Ảnh tham chiếu (reference_images)', type: 'image[]', max: 3, required: true },
    { name: 'reference_videos', label: 'Video tham chiếu (reference_videos)', type: 'video[]' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [4, 6, 8], default: 4 },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['16:9', '9:16'], default: '16:9' },
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['720p', '1080p', '4K'], default: '720p' },
    { name: 'generate_audio', label: 'Tạo âm thanh', type: 'bool', default: true },
  ],
  'seedance-2.0': [
    PROMPT,
    { name: 'input_image_url', label: 'Ảnh đầu vào I2V (input_image_url)', type: 'image' },
    { name: 'reference_images', label: 'Ảnh tham chiếu (reference_images)', type: 'image[]', max: 3 },
    { name: 'reference_videos', label: 'Video tham chiếu (reference_videos)', type: 'video[]' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [5, 8, 10, 12, 15], default: 5 },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['9:16', '16:9', '1:1', '21:9'], default: '16:9' },
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['720p', '480p'], default: '720p' },
    { name: 'generate_audio', label: 'Tạo âm thanh', type: 'bool', default: true },
    { name: 'server', label: 'Server', type: 'select', options: ['business', 'vip-01'] },
  ],
  'seedance-2.0-pro': [
    PROMPT,
    { name: 'input_image_url', label: 'Ảnh đầu vào I2V (input_image_url)', type: 'image' },
    { name: 'reference_images', label: 'Ảnh tham chiếu (reference_images)', type: 'image[]', max: 3 },
    { name: 'reference_videos', label: 'Video tham chiếu (reference_videos)', type: 'video[]' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [5, 8, 10, 12, 15], default: 5 },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['1:1', '9:16', '16:9', '21:9', '4:3', '3:4'], default: '16:9' },
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['720p', '1080p', '480p'], default: '720p' },
    { name: 'generate_audio', label: 'Tạo âm thanh', type: 'bool', default: true },
    { name: 'server', label: 'Server', type: 'select', options: ['rw', 'business'] },
  ],
  'kling-3-omni-video': [
    PROMPT,
    { name: 'input_image_url', label: 'Ảnh đầu vào I2V (input_image_url)', type: 'image' },
    { name: 'reference_images', label: 'Ảnh tham chiếu (reference_images)', type: 'image[]', max: 3 },
    { name: 'reference_videos', label: 'Video tham chiếu (reference_videos)', type: 'video[]' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [3, 5, 6, 7, 8, 10, 12, 15], default: 3 },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['1:1', '9:16', '4:3', '3:4', '16:9'], default: '16:9' },
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['720p', '1080p', '4k'], default: '720p' },
    { name: 'generate_audio', label: 'Tạo âm thanh', type: 'bool', default: true },
    { name: 'server', label: 'Server', type: 'select', options: ['vip-01', 'business'] },
  ],
  // Omni Flash R2V: T2V (chi prompt), I2V (input_image_url), R2V (reference_images toi da 7 anh).
  'omni-flash-video': [
    PROMPT,
    { name: 'input_image_url', label: 'Ảnh đầu vào I2V (input_image_url)', type: 'image' },
    { name: 'reference_images', label: 'Ảnh tham chiếu (reference_images)', type: 'image[]', max: 7 },
    { name: 'reference_videos', label: 'Video tham chiếu (reference_videos)', type: 'video[]' },
    { name: 'duration', label: 'Duration (s)', type: 'select', options: [4, 6, 8, 10], default: 4 },
    { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: ['16:9', '9:16'], default: '16:9' },
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['720p', '1080p', '4k'], default: '720p' },
    { name: 'generate_audio', label: 'Tạo âm thanh', type: 'bool', default: true },
  ],

  // ---------- Dieu khien chuyen dong ----------
  'kling-motion': [
    { name: 'character_image_url', label: 'Ảnh nhân vật (character_image_url)', type: 'image', required: true },
    { name: 'video_reference_url', label: 'Video chuyển động (video_reference_url)', type: 'video', required: true },
    { name: 'prompt', label: 'Prompt (bối cảnh)', type: 'textarea' },
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['720p', '1080p'], default: '720p' },
    { name: 'character_orientation', label: 'Hướng nhân vật', type: 'select', options: ['video', 'image'], default: 'video' },
    { name: 'original_sound', label: 'Giữ âm thanh gốc', type: 'bool', default: true },
    { name: 'server', label: 'Server', type: 'select', options: ['business', 'cheap'] },
  ],
  'kling-motion-30': [
    { name: 'character_image_url', label: 'Ảnh nhân vật (character_image_url)', type: 'image', required: true },
    { name: 'video_reference_url', label: 'Video chuyển động (video_reference_url)', type: 'video', required: true },
    { name: 'prompt', label: 'Prompt (bối cảnh)', type: 'textarea' },
    { name: 'resolution', label: 'Resolution', type: 'select', options: ['1080p', '720p'], default: '720p' },
    { name: 'character_orientation', label: 'Hướng nhân vật', type: 'select', options: ['video', 'image'], default: 'video' },
    { name: 'original_sound', label: 'Giữ âm thanh gốc', type: 'bool', default: true },
    { name: 'server', label: 'Server', type: 'select', options: ['cheap', 'business'] },
  ],
};

// Mac dinh theo nhom endpoint (cho model chua khai bao cu the, vd them moi).
function byEndpoint(endpoint?: string | null): ParamField[] {
  const e = endpoint || '';
  if (e.includes('/text-gen/generate'))
    return [
      PROMPT,
      { name: 'system_instruction', label: 'System instruction', type: 'textarea' },
      { name: 'temperature', label: 'Temperature (0–2)', type: 'number', default: 0.7 },
      { name: 'max_tokens', label: 'Max tokens', type: 'number', default: 2048 },
      { name: 'images', label: 'Ảnh (multi-modal)', type: 'image[]' },
    ];
  const AR = ['Auto', '1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '21:9', '4:5', '5:4'];
  const DUR = [3, 4, 5, 6, 7, 8, 10, 12, 15];
  const QUAL = ['720p', '1080p', '4K'];
  const RES = ['1k', '2k', '4k', '720p', '1080p', '480p'];
  if (e.includes('/generate/image'))
    return [
      PROMPT,
      { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: AR },
      { name: 'resolution', label: 'Resolution', type: 'select', options: ['low', 'medium', 'high', '1k', '2k', '4k'] },
      { name: 'references_urls', label: 'Ảnh tham chiếu (references_urls)', type: 'image[]' },
      NEG,
    ];
  if (e.includes('/generate/video-omni'))
    return [
      PROMPT,
      { name: 'input_image_url', label: 'Ảnh đầu vào I2V', type: 'image' },
      { name: 'reference_images', label: 'Ảnh tham chiếu', type: 'image[]' },
      { name: 'reference_videos', label: 'Video tham chiếu', type: 'video[]' },
      { name: 'duration', label: 'Duration (s)', type: 'select', options: DUR },
      { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: AR },
      { name: 'resolution', label: 'Resolution', type: 'select', options: RES },
      { name: 'generate_audio', label: 'Tạo âm thanh', type: 'bool', default: true },
    ];
  if (e.includes('/generate/motion-control'))
    return [
      { name: 'character_image_url', label: 'Ảnh nhân vật', type: 'image', required: true },
      { name: 'video_reference_url', label: 'Video chuyển động', type: 'video', required: true },
      { name: 'prompt', label: 'Prompt', type: 'textarea' },
      { name: 'resolution', label: 'Resolution', type: 'select', options: ['720p', '1080p'] },
      { name: 'character_orientation', label: 'Hướng nhân vật', type: 'select', options: ['video', 'image'], default: 'video' },
    ];
  if (e.includes('/generate/video'))
    return [
      PROMPT,
      { name: 'mode', label: 'Mode', type: 'select', options: ['t2v', 'i2v'] },
      { name: 'duration', label: 'Duration (s)', type: 'select', options: DUR },
      { name: 'quality', label: 'Quality', type: 'select', options: QUAL },
      { name: 'aspect_ratio', label: 'Aspect ratio', type: 'select', options: AR },
      { name: 'start_image_url', label: 'Ảnh Start Frame', type: 'image' },
      { name: 'end_image_url', label: 'Ảnh End Frame', type: 'image' },
    ];
  return [PROMPT];
}

// ---- Plenxai Free (webhook, multipart) ----
// Field name PHAI khop dung ten multipart cua Plenxai (aspectRatio, quality, images...).
// Code model trung voi plenxai-goc nen KHONG dung BY_CODE — phan biet theo submitEndpoint.
const PF_IMAGE_AR = ['1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3', '4:5', '5:4', 'Auto'];
function plenxaiFreeParams(endpoint?: string | null, code?: string): ParamField[] | null {
  const e = endpoint || '';
  if (e.includes('/api/upscale/generate')) {
    // Klein: chi can anh + mode. PlanX: them resolution/aspectRatio/imageType (an khi klein).
    const planx = { field: 'mode', equals: 'planx' } as const;
    return [
      { name: 'image', label: 'Ảnh nguồn (image)', type: 'image', required: true },
      { name: 'mode', label: 'Chế độ (mode)', type: 'select', options: ['klein', 'planx'], default: 'klein' },
      { name: 'resolution', label: 'Resolution (PlanX)', type: 'select', options: ['2k', '4k'], default: '2k', showWhen: planx },
      { name: 'aspectRatio', label: 'Aspect ratio (PlanX)', type: 'select', options: ['3:4', '4:3', '1:1', '16:9', '9:16'], showWhen: planx },
      { name: 'imageType', label: 'Loại ảnh (PlanX)', type: 'select', options: ['default', 'portrait', 'architecture'], default: 'default', showWhen: planx },
    ];
  }
  if (e.includes('/api/image/generate')) {
    // gpt-image-2: quality = low/medium/high + bat buoc resolution 1k/2k/4k. Con lai: quality = 1k/2k/4k.
    const isGpt = code === 'gpt-image-2';
    const fields: ParamField[] = [
      PROMPT,
      { name: 'aspectRatio', label: 'Aspect ratio', type: 'select', options: PF_IMAGE_AR, default: '1:1' },
      {
        name: 'quality',
        label: 'Quality',
        type: 'select',
        options: isGpt ? ['low', 'medium', 'high'] : ['1k', '2k', '4k'],
        default: isGpt ? 'medium' : '1k',
      },
    ];
    if (isGpt)
      fields.push({ name: 'resolution', label: 'Resolution', type: 'select', options: ['1k', '2k', '4k'], default: '2k' });
    fields.push(
      { name: 'quantity', label: 'Số lượng (1–4)', type: 'number', default: 1 },
      { name: 'images', label: 'Ảnh tham chiếu (images)', type: 'image[]', max: 10 },
      { name: 'freeMode', label: 'Dùng quota miễn phí (freeMode)', type: 'bool', default: false },
    );
    return fields;
  }
  return null;
}

/** Tra ve danh sach tham so cho 1 model (Plenxai Free theo endpoint, roi code, roi endpoint chung). */
export function getModelParams(model: ModelParamsInput): ParamField[] {
  const pf = plenxaiFreeParams(model.submitEndpoint, model.code);
  if (pf) return pf;
  return BY_CODE[model.code] ?? byEndpoint(model.submitEndpoint);
}

export const UPLOAD_TYPES: ParamType[] = ['image', 'video', 'audio', 'media', 'image[]', 'video[]'];

/** Tat ca code model co schema tinh (BY_CODE) — phuc vu conformance test phu toan bo. */
export const STATIC_MODEL_CODES: string[] = Object.keys(BY_CODE);

// =====================================================================
//  TU VUNG THAM SO CANONICAL (P1) — 1 chuan cho MOI model.
//  Bo thu 3 LUON gui ten canonical; Gateway tu dinh tuyen sang ten THAT cua tung
//  model (theo schema cua model do). Khong can cau hinh rieng tung model.
//  Vd: gui `reference_images` -> model nao dung `references_urls`/`images`/... cung chay.
// =====================================================================
export const CANONICAL_ALIASES: Record<string, string[]> = {
  // Nhieu anh tham chieu (mang URL). `image_url` (vd ZORA gpt-img-2 dung image[]) cung gom vao day.
  // CHI CHUA URL ANH — link video/audio bi loai o sanitizeInputForSchema (xem duoi).
  reference_images: [
    'references_urls', 'reference_images', 'images', 'image_urls', 'img_urls',
    'reference_image_urls', 'startImageUrls', 'image_url',
  ],
  // 1 anh dau vao (i2i / i2v — khung hinh dau). `img_url` (TramSangTao Veo/Kling) la ten tho cua no.
  input_image: ['input_image', 'input_image_url', 'img_url', 'start_image', 'start_image_url', 'character_image_url'],
  input_audio: ['input_audio', 'audio', 'audio_url'],
  // 1 video dau vao (video nguon / chinh sua).
  input_video: ['input_video', 'video', 'video_url', 'video_reference_url', 'motion_video_url'],
  // Nhieu video tham chieu (mang URL) — CHI CHUA URL VIDEO.
  reference_videos: ['reference_videos', 'video_urls', 'videos', 'videoRefUrls', 'reference_video_urls'],
  aspect_ratio: ['aspect_ratio', 'ratio'],
  resolution: ['resolution', 'size'],
  negative_prompt: ['negative_prompt'],
  num_outputs: ['num_outputs', 'n', 'quantity', 'num_images'],
  prompt: ['prompt'],
};

/** Ten canonical cua 1 field tho (de catalog phoi canonical). Khong khop -> tra chinh no. */
export function canonicalName(rawName: string): string {
  if (CANONICAL_ALIASES[rawName]) return rawName; // ban than la canonical
  for (const [canon, aliases] of Object.entries(CANONICAL_ALIASES)) {
    if (aliases.includes(rawName)) return canon;
  }
  return rawName;
}

/**
 * Anh xa ten canonical (bo thu 3 gui) -> ten THAT model nhan, theo schema model.
 * - Giu nguyen field model dung dung ten canonical.
 * - Field tho cu (vd references_urls) van duoc chap nhan (backward-compat) — khong dung.
 * - Neu model khong co field tuong ung -> giu nguyen (P2 co the canh bao rieng).
 * Khong schema -> no-op an toan (tra chinh input).
 */
export function applyCanonicalAliases(input: any, schema?: ParamField[] | null): any {
  if (!input || typeof input !== 'object' || !Array.isArray(schema) || !schema.length) return input;
  const names = new Set(schema.map((f) => f.name));
  let out: Record<string, any> | null = null;
  for (const [canon, aliases] of Object.entries(CANONICAL_ALIASES)) {
    if (!(canon in (input as any))) continue; // bo thu 3 khong gui canonical nay
    if (names.has(canon)) continue; // model dung dung ten canonical -> giu
    // Uu tien field CUNG SO LUONG (mang <-> mang, don <-> don). Khong co thi thu sang
    // canonical "chi em" khac so luong (vd nguon chi co `video_url` don ma client gui
    // `reference_videos`) — sanitizeInputForSchema se cat/boc mang cho khop kieu field.
    const fallback = ARITY_SIBLING[canon];
    const candidates = fallback ? [...aliases, ...(CANONICAL_ALIASES[fallback] || [])] : aliases;
    const target = candidates.find((a) => a !== canon && names.has(a));
    if (!target) continue; // model khong co field tuong ung -> giu nguyen
    if (!out) out = { ...(input as any) };
    const dst = out as Record<string, any>;
    dst[target] = (input as any)[canon];
    delete dst[canon];
  }
  return out || input;
}

/** Canonical "chi em" khac so luong: mang <-> don (dung khi nguon chi co 1 trong 2 dang). */
const ARITY_SIBLING: Record<string, string> = {
  reference_images: 'input_image',
  input_image: 'reference_images',
  reference_videos: 'input_video',
  input_video: 'reference_videos',
};

// =====================================================================
//  LAM SACH INPUT THEO SCHEMA (P3) — "1 model = chay tat ca".
//  Bo thu 3 chi biet ten canonical, KHONG biet tung nguon nhan anh/video ra sao.
//  Ham nay sua 3 loi kinh dien TRUOC KHI gui len nguon:
//    1) SAI LOAI MEDIA: link .mp4 lot vao field ANH (reference_images) hoac nguoc lai
//       -> loai bo (kem canh bao), khong de nguon tra "invalid_video_url"/anh loi.
//    2) SAI SO LUONG: field don (video) nhan mang -> lay phan tu dau; field mang nhan
//       chuoi -> boc thanh mang (vd canonical `reference_videos` -> `video_url` don).
//    3) SAI DANG GIA TRI: select/number/bool nhan gia tri tuong duong khac dinh dang
//       (vd duration "8" -> "8s" hoac 8; "true" -> true) -> quy ve option cua nguon.
// =====================================================================

const IMAGE_EXT = /\.(jpe?g|png|webp|gif|bmp|tiff?|heic|heif|avif|jfif|svg)(\?|#|$)/i;
const VIDEO_EXT = /\.(mp4|mov|webm|mkv|avi|m4v|mpe?g|wmv|flv|3gp|ts)(\?|#|$)/i;
const AUDIO_EXT = /\.(mp3|wav|m4a|aac|ogg|oga|flac|opus|wma|aiff?)(\?|#|$)/i;

/** Loai media suy tu duoi URL/duong dan (khong doan duoc -> null, KHONG loai bo). */
export function mediaKindOfUrl(value: unknown): 'image' | 'video' | 'audio' | null {
  if (typeof value !== 'string' || !value) return null;
  const path = value.split(/[?#]/)[0];
  if (/^data:image\//i.test(value)) return 'image';
  if (/^data:video\//i.test(value)) return 'video';
  if (/^data:audio\//i.test(value)) return 'audio';
  if (IMAGE_EXT.test(path)) return 'image';
  if (VIDEO_EXT.test(path)) return 'video';
  if (AUDIO_EXT.test(path)) return 'audio';
  return null;
}

/** Loai media MA field nay chap nhan (null = khong phai field media). */
function acceptedKinds(type: ParamType): Array<'image' | 'video' | 'audio'> | null {
  switch (type) {
    case 'image':
    case 'image[]':
      return ['image'];
    case 'video':
    case 'video[]':
      return ['video'];
    case 'audio':
      return ['audio'];
    case 'media':
      return ['image', 'video'];
    default:
      return null;
  }
}

const ARRAY_TYPES: ParamType[] = ['image[]', 'video[]'];

/** So sanh "tuong duong" 2 gia tri option: khong phan biet hoa/thuong + so hoc (8 ~ "8s" ~ "8.0"). */
function sameOptionValue(a: unknown, b: unknown): boolean {
  const sa = String(a ?? '').trim();
  const sb = String(b ?? '').trim();
  if (!sa || !sb) return false;
  if (sa.toLowerCase() === sb.toLowerCase()) return true;
  const na = Number(String(sa).replace(/[^\d.-]/g, ''));
  const nb = Number(String(sb).replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(na) || !Number.isFinite(nb) || na !== nb) return false;
  // Chi coi la tuong duong khi phan chu con lai giong nhau hoac mot ben khong co chu
  // (8 ~ "8s" OK; "720p" ~ "720" OK; nhung "4k" ~ "4s" thi KHONG).
  const ua = sa.replace(/[\d.\s-]/g, '').toLowerCase();
  const ub = sb.replace(/[\d.\s-]/g, '').toLowerCase();
  return !ua || !ub || ua === ub;
}

/**
 * Lam sach input theo schema THAT cua nguon (model le / member cua nhom).
 * Tra ve input moi (khong mutate) + danh sach canh bao de lo qua meta.warnings.
 * Khong co schema -> no-op an toan.
 */
export function sanitizeInputForSchema(
  input: any,
  schema?: ParamField[] | null,
): { input: any; warnings: string[] } {
  const warnings: string[] = [];
  if (!input || typeof input !== 'object' || !Array.isArray(schema) || !schema.length) {
    return { input, warnings };
  }
  const out: Record<string, any> = { ...input };
  let changed = false;

  for (const f of schema) {
    if (!(f.name in out)) continue;
    const raw = out[f.name];
    if (raw == null || raw === '') continue;

    const kinds = acceptedKinds(f.type);
    const isArrayField = ARRAY_TYPES.includes(f.type);

    if (kinds) {
      // (1) LOAI SAI LOAI MEDIA
      const list = Array.isArray(raw) ? raw : [raw];
      const kept: any[] = [];
      for (const v of list) {
        const kind = mediaKindOfUrl(v);
        if (kind && !kinds.includes(kind)) {
          warnings.push(
            `${f.name}: bo qua 1 link ${kind} (field nay chi nhan ${kinds.join('/')}) — ${String(v).slice(0, 120)}`,
          );
          continue;
        }
        kept.push(v);
      }
      // (2) SUA SO LUONG cho khop kieu field
      let next: any;
      if (isArrayField) {
        next = kept;
        if (typeof f.max === 'number' && f.max > 0 && kept.length > f.max) {
          warnings.push(`${f.name}: nguon chi nhan toi da ${f.max} — da cat bo ${kept.length - f.max} link cuoi`);
          next = kept.slice(0, f.max);
        }
      } else {
        next = kept.length ? kept[0] : '';
        if (kept.length > 1)
          warnings.push(`${f.name}: nguon chi nhan 1 link — da dung link dau tien, bo ${kept.length - 1} link con lai`);
      }
      const emptyNow = isArrayField ? !(next as any[]).length : !next;
      if (emptyNow) {
        delete out[f.name]; // khong gui field rong (de nguon dung mac dinh / chay che do khac)
        changed = true;
      } else if (JSON.stringify(next) !== JSON.stringify(raw)) {
        out[f.name] = next;
        changed = true;
      }
      continue;
    }

    // (3) QUY VE DANG GIA TRI CUA NGUON
    if (f.type === 'select' && Array.isArray(f.options) && f.options.length) {
      // Khop chinh xac theo chuoi -> dung KIEU cua option (vd Kapend duration nhan SO 8,
      // client gui "8"); khong khop -> thu tuong duong (vd "8" -> "8s", "720" -> "720p").
      const exact = f.options.find((o) => String(o) === String(raw));
      const match = exact !== undefined ? exact : f.options.find((o) => sameOptionValue(o, raw));
      if (match !== undefined && match !== raw) {
        out[f.name] = match;
        changed = true;
      }
    } else if (f.type === 'number') {
      if (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))) {
        out[f.name] = Number(raw);
        changed = true;
      }
    } else if (f.type === 'bool') {
      if (typeof raw === 'string') {
        const s = raw.trim().toLowerCase();
        if (['true', '1', 'yes', 'on'].includes(s)) {
          out[f.name] = true;
          changed = true;
        } else if (['false', '0', 'no', 'off'].includes(s)) {
          out[f.name] = false;
          changed = true;
        }
      }
    }
  }

  return { input: changed ? out : input, warnings };
}

/**
 * Loai tru tham so theo che do cua nguon (`ProviderModel.config.exclusiveFields`).
 * Vd TramSangTao veo3.1-omni: co `video_url` = che do CHINH SUA Omni -> `img_url`
 * (khung hinh dau i2v) khong con y nghia va lam nguon hieu sai yeu cau -> bo.
 * Dinh dang: [{ when: 'video_url', drop: ['img_url'], note?: '...' }]
 */
export interface ExclusiveFieldRule {
  when: string;
  drop: string[];
  note?: string;
}

export function applyExclusiveFields(
  input: any,
  rules?: ExclusiveFieldRule[] | null,
): { input: any; warnings: string[] } {
  const warnings: string[] = [];
  if (!input || typeof input !== 'object' || !Array.isArray(rules) || !rules.length) return { input, warnings };
  let out: Record<string, any> | null = null;
  for (const rule of rules) {
    if (!rule?.when || !Array.isArray(rule.drop)) continue;
    const v = (input as any)[rule.when];
    const present = Array.isArray(v) ? v.length > 0 : v != null && v !== '';
    if (!present) continue;
    for (const name of rule.drop) {
      const cur = (out || input)[name];
      const has = Array.isArray(cur) ? cur.length > 0 : cur != null && cur !== '';
      if (!has) continue;
      if (!out) out = { ...(input as any) };
      delete (out as Record<string, any>)[name];
      warnings.push(`${name}: bo qua vi da co ${rule.when}${rule.note ? ` (${rule.note})` : ''}`);
    }
  }
  return { input: out || input, warnings };
}
